/**
 * Word Comment Service
 *
 * Inserts an AI-suggested comment into the Word document on user approval.
 *
 * Reuses the redline tool's paragraph-by-id machinery wholesale — the comment
 * apply needs the same "resolve a backend paragraphId to a live, tracker-adjusted
 * paragraph and search within it" capability the redline EDIT path has. No new
 * search/index code:
 *   - readLiveParagraphText(paragraphId)  — tracker-adjusted index + live text
 *   - searchWithinParagraph(context, adjustedIndex, phrase) — paragraph-scoped
 *     2-tier LENIENT→STRICT search (same primitive applyTextEditOperation uses)
 *
 * Stale handling: if we cannot confirm the right target on the right paragraph we
 * BLOCK (return stale) and tell the model the content changed — never relocate
 * silently. The anchorPhrase path uses the scoped search above; the
 * whole-paragraph path compares the backend anchor text against the live
 * paragraph text with an exact 1:1 match. There is no document-wide search
 * fallback; misplacing a comment is worse than a clear "content changed" message.
 *
 * The service never throws to the card — every failure path maps to a result.
 */

/* global Office, Word */

import { AddCommentOutput } from "../../../interfaces/addCommentTypes";
import { readLiveParagraphText } from "../../autoRedline/services/wordSequentialEditService";
import { searchWithinParagraph, SearchResult } from "../../autoRedline/services/wordDocumentSearch";
import { createLogger } from "../../../utils/logger";

const log = createLogger("WordCommentService");

export interface InsertCommentResult {
  success: boolean;
  /** True when the apply was blocked because the target text changed. */
  stale?: boolean;
  /** Human-readable error (failed or unsupported-host cases). */
  error?: string;
}

/**
 * Does this thrown error come from searchWithinParagraph's bounds / phrase
 * validation? Those signal the targeted paragraph/phrase no longer lines up
 * (→ stale). Any OTHER throw (Word.run blew up unrelated to bounds) should NOT
 * false-block — the caller falls through to the insert (mirrors auto_redline
 * AutoRedlinePreview.tsx:326-335).
 */
function isBoundsValidationError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message;
  return (
    msg.includes("out of bounds") ||
    msg.includes("must be >= 0") ||
    msg.includes("255-character limit") ||
    msg.includes("cannot be empty")
  );
}

/**
 * Reduce a paragraph to the same character representation the BACKEND used when
 * it built `anchorParagraphText`, so the whole-paragraph compare is
 * apples-to-apples. Two adjustments, each undoing a non-content difference
 * between Word's live `paragraph.text` and the backend's XML-derived snapshot:
 *
 *   1. Non-breaking space (U+00A0) → regular space. The backend's
 *      `_build_anchor_paragraph_text` does `.replace("\xa0", " ")` (matching
 *      auto-redline's `clean_search_paragraph`), so the snapshot has no nbsp
 *      while Word's live text keeps them.
 *   2. Drop Unicode control characters (C0 0x00-0x1F, DEL 0x7F, C1 0x80-0x9F) —
 *      Word injects the comment reference mark U+0005 at every existing comment
 *      anchor (and a trailing paragraph mark) that the backend XML never has.
 *
 * Real content differences are still compared exactly; only these known backend
 * transforms / Word runtime artifacts are reconciled.
 */
function normalizeForExactCompare(text: string): string {
  let out = "";
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0;
    if (code <= 0x1f || (code >= 0x7f && code <= 0x9f)) continue; // drop control chars
    out += code === 0xa0 ? " " : ch; // nbsp → space (mirror the backend)
  }
  return out;
}

/**
 * Insert the suggested comment at the targeted location.
 *
 * @param output The add-comment output from the backend.
 * @returns A result object — never throws.
 */
export async function insertDocumentComment(
  output: AddCommentOutput
): Promise<InsertCommentResult> {
  try {
    // Step 1: Guard the WordApi 1.4 requirement (Range.insertComment).
    const supportsWordApi14 =
      Office.context.requirements?.isSetSupported?.("WordApi", "1.4") ?? false;
    if (!supportsWordApi14) {
      log.warn("insertDocumentComment: WordApi 1.4 unsupported on this host", {
        sectionName: output.sectionName,
      });
      return {
        success: false,
        error:
          "Comments aren't supported in this version of Word. Please try in a newer Word build.",
      };
    }

    // Step 2: Resolve the backend paragraphId to a live, tracker-adjusted index.
    // outOfBounds → the targeted paragraph no longer exists at that position →
    // stale. No document-wide search.
    const live = await readLiveParagraphText(output.paragraphId);
    if (live.outOfBounds) {
      log.info("insertDocumentComment: paragraph out of bounds → stale", {
        sectionName: output.sectionName,
        paragraphId: output.paragraphId,
        adjustedIndex: live.adjustedIndex,
      });
      return { success: false, stale: true };
    }
    const adjustedIndex = live.adjustedIndex;

    const hasAnchorPhrase = !!output.anchorPhrase && output.anchorPhrase.length > 0;

    // Step 3: Stale guard + insert in a single Word.run.
    return await Word.run(async (context) => {
      let commentRange: Word.Range;

      if (hasAnchorPhrase) {
        // anchorPhrase path — the precise target. Reuse the redline tool's
        // scoped 2-tier search so "is the phrase still there?" inherits the
        // lenient/strict matching instead of a hand-rolled .includes().
        let search: SearchResult | undefined;
        try {
          search = await searchWithinParagraph(
            context,
            adjustedIndex,
            output.anchorPhrase as string
          );
        } catch (guardError) {
          if (isBoundsValidationError(guardError)) {
            // The phrase/paragraph no longer lines up → stale.
            log.info("insertDocumentComment: anchorPhrase bounds error → stale", {
              sectionName: output.sectionName,
              paragraphId: output.paragraphId,
              error: guardError instanceof Error ? guardError.message : guardError,
            });
            return { success: false, stale: true };
          }
          // Non-bounds guard throw — don't false-block. Fall through to the
          // insert on the whole paragraph (mirrors auto_redline :326-335).
          log.warn(
            "insertDocumentComment: anchorPhrase guard threw (non-bounds); proceeding with whole-paragraph insert",
            {
              sectionName: output.sectionName,
              paragraphId: output.paragraphId,
              error: guardError instanceof Error ? guardError.message : guardError,
            }
          );
          search = undefined;
        }

        if (search) {
          if (search.totalOccurrences === 0) {
            // The sentence we wanted to annotate was edited, or lived in a
            // redline the user rejected / hasn't applied. Phrase-level guard: an
            // unrelated applied redline elsewhere in the same paragraph leaves
            // the phrase intact so the comment still lands, but a change to the
            // annotated text itself blocks rather than mis-anchors.
            log.info("insertDocumentComment: anchorPhrase not found → stale", {
              sectionName: output.sectionName,
              paragraphId: output.paragraphId,
            });
            return { success: false, stale: true };
          }
          // First hit. A paragraph-scoped occurrence index can be added later
          // (mirroring searchPhraseOccurrence) if duplicate phrases ever bite.
          commentRange = search.results.items[0];
        } else {
          // Guard fell through (non-bounds throw) — anchor on the whole paragraph.
          commentRange = await resolveWholeParagraphRange(context, adjustedIndex);
        }
      } else {
        // Whole-paragraph comment — exact auto_redline guard: compare the BE
        // strip_tags snapshot (deletions + insertions inline) against the live
        // paragraph text. This holds while tracked changes are unapplied.
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items");
        await context.sync();

        if (adjustedIndex < 0 || adjustedIndex >= paragraphs.items.length) {
          // Defensive: the document changed between readLiveParagraphText and
          // here. Treat as stale.
          log.info("insertDocumentComment: adjustedIndex out of bounds inside run → stale", {
            sectionName: output.sectionName,
            paragraphId: output.paragraphId,
            adjustedIndex,
          });
          return { success: false, stale: true };
        }

        const targetParagraph = paragraphs.items[adjustedIndex];
        targetParagraph.load("text");
        await context.sync();

        // Exact content comparison, after reconciling the backend's own text
        // transforms (nbsp→space) and Word runtime artifacts (control marks) so
        // the two representations of identical content line up. See
        // normalizeForExactCompare for the full rationale.
        const beText = normalizeForExactCompare(output.anchorParagraphText);
        const liveText = normalizeForExactCompare(targetParagraph.text);
        if (beText !== liveText) {
          // Pinpoint the first divergence so a residual mismatch is diagnosable:
          // a control-char code (5, 13, …) means a runtime mark we should also
          // strip; a normal char means the backend genuinely altered the content.
          let diffAt = 0;
          const minLen = Math.min(beText.length, liveText.length);
          while (diffAt < minLen && beText[diffAt] === liveText[diffAt]) diffAt++;
          const codesAround = (s: string, i: number) =>
            Array.from(s.slice(Math.max(0, i - 2), i + 3)).map((c) => c.codePointAt(0));
          log.info("insertDocumentComment: whole-paragraph mismatch → stale", {
            sectionName: output.sectionName,
            paragraphId: output.paragraphId,
            beLength: beText.length,
            liveLength: liveText.length,
            diffAt,
            beCodesAtDiff: codesAround(beText, diffAt),
            liveCodesAtDiff: codesAround(liveText, diffAt),
            beSnippet: beText.slice(Math.max(0, diffAt - 12), diffAt + 12),
            liveSnippet: liveText.slice(Math.max(0, diffAt - 12), diffAt + 12),
          });
          return { success: false, stale: true };
        }

        // Same direct-range path the redline tool uses for empty-searchPhrase EDITs.
        commentRange = targetParagraph.getRange("Whole");
      }

      // Step 4: Insert the comment and sync.
      // NOTE (Word for the web, office-js#5323): insertComment persists the
      // comment but the web client does NOT render it until the page is
      // reloaded — the API still returns success with no error, so a green
      // "applied" tick with no visible comment is expected on web until refresh.
      // This is a Microsoft-side display bug with no API workaround; desktop is
      // unaffected. Do not treat "comment not visible immediately on web" as a
      // failure of this code.
      commentRange.insertComment(output.commentText);
      await context.sync();

      log.info("insertDocumentComment: comment inserted", {
        sectionName: output.sectionName,
        paragraphId: output.paragraphId,
        anchorMode: hasAnchorPhrase ? "phrase" : "whole-paragraph",
      });

      return { success: true };
    });
  } catch (error) {
    // Catch everything — never throw to the card.
    log.warn("insertDocumentComment: insert failed", {
      sectionName: output.sectionName,
      paragraphId: output.paragraphId,
      error: error instanceof Error ? error.message : error,
    });
    return {
      success: false,
      error: error instanceof Error ? error.message : "Failed to add the comment.",
    };
  }
}

/**
 * Load the paragraphs and return the Whole range of the given adjusted index.
 * Used as the fall-through target when the anchorPhrase guard throws a
 * non-bounds error (we still want the comment to land, on the whole paragraph).
 */
async function resolveWholeParagraphRange(
  context: Word.RequestContext,
  adjustedIndex: number
): Promise<Word.Range> {
  const paragraphs = context.document.body.paragraphs;
  paragraphs.load("items");
  await context.sync();
  return paragraphs.items[adjustedIndex].getRange("Whole");
}
